import classnames from 'classnames'
import PropTypes from 'prop-types'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Card,
  CardBody,
  Col,
  Popover,
  PopoverBody,
  PopoverHeader,
  Row
} from 'reactstrap'

import { detectLang } from '../../../lib/helpers'
import Spinner from '../../common/Spinner'
import styles from './PriceItem.module.scss'

const getSalePrice = (price, plan) => {
  plan = plan.toLowerCase()
  switch (plan) {
    case 'micro':
    case 'enterprise':
      return price * 0.7
    case 'basic':
    case 'standard':
      return price * 0.6
    case 'professional':
      return price * 0.5
    default:
      return price
  }
}

const Price = ({
  sale,
  salePrice,
  plan,
  currency,
  yearlyPrice,
  monthlyPrice,
  interval
}) => {
  let yearlyInMonthly = yearlyPrice / 12
  let yearlyInMonthlyRounded = Math.round(yearlyInMonthly * 100) / 100
  if (currency === 'JPY') {
    yearlyInMonthlyRounded = Math.round(yearlyInMonthly)
  }

  let price = interval === 'monthly' ? monthlyPrice : yearlyInMonthlyRounded
  if (salePrice) {
    price = getSalePrice(price, plan)
  }

  const formattedPrice = () => {
    return price
      .toLocaleString('en-US', { style: 'currency', currency: currency })
      .slice(1)
  }
  return (
    <div className="d-inline-block">
      {currency === 'USD' && <span className="priceCurrency">$</span>}
      <span
        className={`priceValue ${classnames({
          strikethrough: sale && !salePrice
        })}`}
      >
        {formattedPrice()}
      </span>
      {currency === 'JPY' && (
        <span className="priceCurrency">
          {detectLang() === 'ja' ? '円' : 'JPY'}
        </span>
      )}
      <style jsx>{`
        .priceValue {
          font-size: 30px;
          font-weight: 400;
          color: #444;
        }

        .priceCurrency {
          font-size: 14px;
          color: #7460b9;
        }

        :global(.bg-dark-purple) .priceValue {
          color: #fff;
        }
        :global(.bg-dark-purple) .priceCurrency {
          color: #ebe5f7;
        }
        .strikethrough {
          margin-right: 5px;
          position: relative;
          opacity: 0.5;
        }
        .price > div:nth-child(1) {
          background: pink;
        }
        .strikethrough:after {
          content: '';
          height: 2px;
          width: 100%;
          position: absolute;
          top: 50%;
          left: 0;
          transform: rotate(15deg);
          background-color: red;
          opacity: 1;
        }
      `}</style>
    </div>
  )
}

const PriceItem = (props) => {
  const { t, i18n } = useTranslation()

  const [viewPopoverOpen, setViewPopoverOpen] = React.useState(false)
  const [limitPopoverOpen, setLimitPopoverOpen] = React.useState(false)
  const toggleViewPopover = () => setViewPopoverOpen(!viewPopoverOpen)
  const toggleLimitPopover = () => setLimitPopoverOpen(!limitPopoverOpen)

  const viewPopoverContent = (
    <>
      <PopoverHeader>
        <strong>{t('Counting views')}</strong>
      </PopoverHeader>
      <PopoverBody>
        <p>
          {t(
            "Unlike other systems we don't count your sites pageviews. We only count a popup view when content is generated by our plugin. If a popup or teaser doesn't show, it's not counted."
          )}
        </p>
        <p>
          {t(
            'Example - If you set a one time welcome popup, this will count as a single view per user. The rest of the users page views will not be counted.'
          )}
        </p>
        <p className="mb-0">{t('Your limit will reset monthly.')}</p>
      </PopoverBody>
    </>
  )

  const limitOverContent = (
    <>
      <PopoverHeader>
        <strong>{t('Exceeding limits')}</strong>
      </PopoverHeader>
      <PopoverBody>
        <p>
          {t(
            "If you happen to exceed your limit, don't worry, you will never be charged extra!"
          )}
        </p>
        <p>
          {t(
            'Your promotions will simply stop displaying until your limit resets at the end of the month or you upgrade.'
          )}
        </p>
      </PopoverBody>
    </>
  )

  return (
    <>
      <div
        className={`priceItem rounded-medium p-4 mb-3 ${
          props.active ? 'active' : ''
        } ${props.highlight ? 'bg-dark-purple' : 'bg-light-purple'} ${
          i18n.language === 'ja' ? 'ja' : 'en'
        } position-relative`}
        onClick={props.onClick}
      >
        {props.planname === 'Micro' && props.sale && (
          <img
            src="/assets/images/sale/30offribbon.png"
            className="saleImage"
          />
        )}
        {props.planname === 'Enterprise' && props.sale && (
          <img
            src="/assets/images/sale/30offribbon.png"
            className="saleImage"
          />
        )}
        {props.planname === 'Basic' && props.sale && (
          <img
            src="/assets/images/sale/40offribbon.png"
            className="saleImage"
          />
        )}
        {props.planname === 'Standard' && props.sale && (
          <img
            src="/assets/images/sale/40offribbon.png"
            className="saleImage"
          />
        )}
        {props.planname === 'Professional' && props.sale && (
          <img
            src="/assets/images/sale/50offribbon.png"
            className="saleImage"
          />
        )}
        {props.active === true && (
          <div className={styles.priceActiveBadge}>{t('Current plan')}</div>
        )}
        <Row>
          <Col>
            <div className="priceHeader d-flex align-items-center mb-2">
              <div className="iconWrap">
                <i className="mdi mdi-calendar-check mdi-24px" />
              </div>
              <span className="priceName">{t(props.planname)}</span>
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            <div className="price mb-3">
              <Price
                plan={props.planname}
                interval={props.billinginterval}
                currency={props.currencySymbol}
                sale={props.sale}
                monthlyPrice={props.monthlyprice}
                yearlyPrice={props.yearlyprice}
              />
              {props.sale && (
                <Price
                  plan={props.planname}
                  interval={props.billinginterval}
                  currency={props.currencySymbol}
                  sale={props.sale}
                  monthlyPrice={props.monthlyprice}
                  yearlyPrice={props.yearlyprice}
                  salePrice={true}
                />
              )}
              {props.billinginterval === 'monthly' && (
                <span className="pricePeriod"> {t('/ month')}</span>
              )}
              {props.billinginterval !== 'monthly' && (
                <span className="pricePeriod"> {t('/ month')} x 12</span>
              )}
            </div>
          </Col>
        </Row>
        {detectLang() === 'en' &&
          props.currencySymbol === 'JPY' &&
          props.monthlyprice > 0 && (
            <Row className="mb-4">
              <Col>
                <strong>
                  Approx ${(props.monthlyprice * 0.0078).toLocaleString()} USD.
                </strong>{' '}
                Exact rate shown at purchase gateway.
              </Col>
            </Row>
          )}
        <Row>
          <Col>
            <div className="priceDescription-wrap">
              <div className="priceDescription mb-5">
                {props.isFree && (
                  <ul>
                    <li>
                      <i className="mdi mdi-check-bold" />
                      <strong>
                        <span>
                          {props.planviews.toLocaleString()} {t('Popup views')}
                        </span>
                      </strong>
                      <br />
                      <div className="small">
                        {t('Each month')} (
                        <span
                          style={{ textDecoration: 'underline' }}
                          onMouseOver={toggleViewPopover}
                          onMouseOut={toggleViewPopover}
                          id={`viewPopover${props.planviews}`}
                        >
                          {t('How do we count?')}
                        </span>
                        )
                        <Popover
                          placement="bottom"
                          isOpen={viewPopoverOpen}
                          target={`viewPopover${props.planviews}`}
                        >
                          {viewPopoverContent}
                        </Popover>
                      </div>
                    </li>
                    <li>
                      <i className="mdi mdi-close" />
                      <span>{t('Remove branding')}</span>
                    </li>
                    <li>
                      <i className="mdi mdi-close" />
                      <span>{t('No autoresponders')}</span>
                    </li>
                    <li>
                      <i className="mdi mdi-check-bold" />
                      <span>{t('Unlimited Popup creation')}</span>
                    </li>
                    <li>
                      <i className="mdi mdi-check-bold" />
                      <span>{t('Unlimited Conversions')}</span>
                    </li>
                    <li>
                      <i className="mdi mdi-check-bold" />
                      <span>{t('Unlimited Contacts')}</span>
                    </li>
                    <li>
                      <i className="mdi mdi-check-bold" />
                      <span>{t('Never charged for exceeding limits')}</span>
                      <div className="small">
                        <span
                          style={{ textDecoration: 'underline' }}
                          onMouseOver={toggleLimitPopover}
                          onMouseOut={toggleLimitPopover}
                          id={`limitPopover${props.planviews}`}
                        >
                          {t('What happens?')}
                        </span>
                        <Popover
                          placement="bottom"
                          isOpen={limitPopoverOpen}
                          target={`limitPopover${props.planviews}`}
                        >
                          {limitOverContent}
                        </Popover>
                      </div>
                    </li>
                  </ul>
                )}
                {!props.isFree && (
                  <ul>
                    <li>
                      <i className="mdi mdi-check-bold" />
                      <strong>
                        <span>
                          {props.plus
                            ? t('~Unlimited')
                            : props.planviews.toLocaleString()}{' '}
                          {t('Popup views')}
                        </span>
                      </strong>
                      <br />
                      <div className="small">
                        {t('Each month')} (
                        <span
                          style={{ textDecoration: 'underline' }}
                          onMouseOver={toggleViewPopover}
                          onMouseOut={toggleViewPopover}
                          onClick={() => {}}
                          id={`viewPopover${props.planviews}`}
                        >
                          {t('How do we count?')})
                        </span>
                        <Popover
                          placement="bottom"
                          isOpen={viewPopoverOpen}
                          target={`viewPopover${props.planviews}`}
                        >
                          {viewPopoverContent}
                        </Popover>
                      </div>
                    </li>
                    <li>
                      <i className="mdi mdi-check-bold" />
                      <span>{t('Remove branding')}</span>
                    </li>
                    <li>
                      <i className="mdi mdi-check-bold" />
                      <span>{t('Live support')}</span>
                    </li>
                    <li>
                      <i className="mdi mdi-check-bold" />
                      <span>{t('Unlimited Autoresponders')}</span>
                    </li>
                    <li>
                      <i className="mdi mdi-check-bold" />
                      <span>{t('Unlimited Popup creation')}</span>
                    </li>
                    <li>
                      <i className="mdi mdi-check-bold" />
                      <span>{t('Unlimited Conversions')}</span>
                    </li>
                    <li>
                      <i className="mdi mdi-check-bold" />
                      <span>{t('Unlimited Contacts')}</span>
                    </li>
                    <li>
                      <i className="mdi mdi-check-bold" />
                      <span>{t('Unlimited A/B tests')}</span>
                    </li>
                  </ul>
                )}
              </div>
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            {props.sale && !props.active && (
              <div
                onClick={(e) => {
                  e.stopPropagation()
                }}
              >
                {props.planname === 'Micro' && (
                  <span className="coupon-code">
                    30% {t('Coupon')}: <strong>F270bfcm30off</strong>
                  </span>
                )}
                {props.planname === 'Enterprise' && (
                  <span className="coupon-code">
                    30% {t('Coupon')}: <strong>F270bfcm30off</strong>
                  </span>
                )}
                {props.planname === 'Basic' && (
                  <span className="coupon-code">
                    40% {t('Coupon')}: <strong>F270bfcm40off2</strong>
                  </span>
                )}
                {props.planname === 'Standard' && (
                  <span className="coupon-code">
                    40% {t('Coupon')}: <strong>F270bfcm40off2</strong>
                  </span>
                )}
                {props.planname === 'Professional' && (
                  <span className="coupon-code">
                    50% {t('Coupon')}: <strong>F270bfcm50off</strong>
                  </span>
                )}
              </div>
            )}

            <div className="priceButton">
              {!props.active === true && (
                <Button
                  color={props.highlight ? 'white' : 'primary'}
                  size="lg"
                  className="d-block py-3 w-100"
                >
                  {props.isFree && <>{t('Select plan')}</>}
                  {!props.isFree && props.enableTrial && (
                    <>{t('Start 7 day trial')}</>
                  )}
                  {!props.isFree && !props.enableTrial && (
                    <>{t('Select plan')}</>
                  )}
                  <i className="mdi mdi-chevron-right" />
                </Button>
              )}
              {props.active === true && (
                <Button color="info" size="lg" className="d-block py-3 w-100">
                  <>
                    {props.isFree
                      ? t('Start using for free')
                      : t('Current plan')}
                  </>
                  <i className="mdi mdi-chevron-right" />
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </div>
      <style jsx>
        {`
          .coupon-code {
            width: 100%;
            padding: 10px 0;
            text-align: center;
            background: white;
            border: 2px dashed #333;
            color: #333;
            display: block;
            margin-bottom: 12px;
          }
          .bg-light-purple {
            background-color: #ebe5f7;
          }

          .bg-dark-purple {
            background-color: #7460b9;
            color: #fff;
          }

          .active {
            background-color: #f4fff7;
          }

          .rounded-medium {
            border-radius: 15px;
          }

          .priceHeader .iconWrap {
            background: #7460b9;
            width: 42px;
            height: 42px;
            border-radius: 5px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #fff;
            margin-right: 16px;
          }

          .pricePeriod {
            font-size: 14px;
            color: #7460b9;
          }

          .bg-dark-purple .pricePeriod {
            color: #ebe5f7;
          }

          .bg-dark-purple .priceHeader .iconWrap {
            background: #fff;
            color: #7460b9;
          }

          .priceName {
            font-size: 24px;
          }

          .ja .priceName {
            font-size: 20px;
          }

          .priceDescription-wrap :global(.priceDescription ul) {
            list-style: none;
            padding-left: 0;
          }

          .priceDescription-wrap :global(.priceDescription ul li) {
            margin-bottom: 8px;
            font-size: 16px;
            position: relative;
            padding-left: 24px;
          }

          .priceDescription-wrap :global(.priceDescription ul i) {
            font-size: 18px;
            color: #162b69;
            padding: 3px;
            border-radius: 5px;
            margin-right: 12px;
            position: absolute;
            left: -10px;
            top: 50%;
            transform: translateY(-50%);
          }

          .priceDescription-wrap :global(.bg-dark-purple .priceDescription ul) {
            color: #fff;
          }

          .priceDescription-wrap :global(.badge) {
            position: absolute;
            top: -5px;
            right: 0;
            font-size: 9px;
          }

          .bg-dark-purple
            .priceDescription-wrap
            :global(.priceDescription ul i) {
            color: #fff;
          }

          .priceItem {
            border: 3px solid #fff;
            transition: all 0.3s;
            cursor: pointer;
          }

          .priceItem:hover {
            border-color: #50a5f1;
          }

          .priceItem.active {
            border-color: #50a5f1;
          }
          .saleImage {
            width: 100px;
            position: absolute;
            top: -6px;
            right: -5px;
          }

          :global(.popover) {
            box-shadow: 6px 6px 15px rgba(0, 0, 0, 0.2);
            max-width: 300px;
          }
        `}
      </style>
    </>
  )
}

PriceItem.defaultProps = {
  planname: 'Not entered',
  planviews: 0,
  monthlyprice: 0,
  yearlyprice: 0,
  billinginterval: 'monthly',
  active: false,
  loading: false,
  currencySymbol: '$',
  enableTrial: true,
  highlight: false,
  isFree: false
}

PriceItem.propTypes = {
  planname: PropTypes.string,
  planviews: PropTypes.number,
  monthlyprice: PropTypes.number,
  yearlyprice: PropTypes.number,
  billinginterval: PropTypes.string,
  active: PropTypes.bool,
  onClick: PropTypes.func,
  currencySymbol: PropTypes.string,
  enableTrial: PropTypes.bool,
  highlight: PropTypes.bool
}

export default PriceItem
